import { audioContextService } from 'features/audio/services';
import { delay } from 'features/utils/helpers';

type DialPadValue =
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '0'
  | '*'
  | '#';

interface Frequencies {
  f1: number;
  f2: number;
}

/**
 * Plays DTMF sounds in a queued manner with fade-in/out.
 */
export class DtmfSound {
  private osc1: OscillatorNode | null = null;
  private osc2: OscillatorNode | null = null;
  private gainNode: GainNode | null = null;
  private isDestroying = false;
  private readonly playQueue: DialPadValue[] = [];
  private isProcessingQueue = false;

  private readonly dtmfFrequencies: Record<DialPadValue, Frequencies> = {
    '1': { f1: 497, f2: 1009 },
    '2': { f1: 497, f2: 1136 },
    '3': { f1: 497, f2: 1277 },
    '4': { f1: 570, f2: 1009 },
    '5': { f1: 570, f2: 1136 },
    '6': { f1: 570, f2: 1277 },
    '7': { f1: 652, f2: 1009 },
    '8': { f1: 652, f2: 1136 },
    '9': { f1: 652, f2: 1277 },
    '0': { f1: 741, f2: 1136 },
    '*': { f1: 741, f2: 1009 },
    '#': { f1: 741, f2: 1277 },
  };

  public addToQueue(value: DialPadValue | number) {
    const dialValue = typeof value === 'number' ? value.toString() : value;
    if (!this.isValidDtmfTone(dialValue)) {
      throw new Error(`Invalid DTMF tone: ${dialValue}`);
    }

    this.playQueue.push(dialValue);
    if (!this.isProcessingQueue) {
      void this.processQueue();
    }
  }

  private async processQueue(): Promise<void> {
    this.isProcessingQueue = true;

    while (this.playQueue.length > 0) {
      const nextValue = this.playQueue.shift();
      if (!nextValue) {
        break;
      }

      try {
        await this.playTone(nextValue);
      } catch (error) {
        console.error('Play tone error:', error);
      }
    }

    this.isProcessingQueue = false;
  }

  private async playTone(value: DialPadValue): Promise<void> {
    const { f1, f2 } = this.dtmfFrequencies[value];
    await this.setupOscillators(f1, f2);
    await this.startOscillators();
    await delay(100);
    await this.stopOscillators();
  }

  /**
   * Create oscillator and gain/filter nodes and connect them.
   */
  private async setupOscillators(f1: number, f2: number) {
    const context = audioContextService.getContext();
    this.osc1 = context.createOscillator();
    this.osc2 = context.createOscillator();

    this.osc1.frequency.value = f1;
    this.osc2.frequency.value = f2;

    this.gainNode = context.createGain();
    this.gainNode.gain.value = 0;

    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';

    this.osc1.connect(this.gainNode);
    this.osc2.connect(this.gainNode);
    this.gainNode.connect(filter);
    filter.connect(context.destination);
  }

  private async startOscillators() {
    try {
      this.osc1?.start();
      this.osc2?.start();

      const context = audioContextService.getContext();
      const now = context.currentTime;
      this.gainNode?.gain.setValueAtTime(0, now);
      this.gainNode?.gain.linearRampToValueAtTime(0.25, now + 0.01);
    } catch (error) {
      console.error('Start oscillators error:', error);
    }
  }

  /**
   * Fade out over 50 ms, stop, then disconnect for more smooth tones that don't overlap
   */
  private async stopOscillators() {
    if (!this.osc1 || !this.osc2 || !this.gainNode) {
      return;
    }

    const context = audioContextService.getContext();
    const now = context.currentTime;
    const fadeTime = 0.05;

    this.gainNode.gain.cancelScheduledValues(now);
    this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, now);
    this.gainNode.gain.exponentialRampToValueAtTime(0.0001, now + fadeTime);

    await delay(50);

    try {
      this.osc1.stop();
      this.osc2.stop();
      this.osc1.disconnect();
      this.osc2.disconnect();
      this.gainNode.disconnect();
    } catch (error) {
      console.error('Stop/disconnect error:', error);
    }

    this.osc1 = null;
    this.osc2 = null;
    this.gainNode = null;
  }

  public async destroy(): Promise<void> {
    if (this.isDestroying) {
      return;
    }
    this.isDestroying = true;

    this.isProcessingQueue = false;
    this.playQueue.length = 0;
    await this.stopOscillators();

    this.isDestroying = false;
  }

  private isValidDtmfTone(value: string): value is DialPadValue {
    return Object.keys(this.dtmfFrequencies).includes(value);
  }
}
